import { Layout, LoadingSpinner } from "@oysterhr/ui"
import { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { amplitudeLogEvent } from "~/app/utils/amplitudeHelpers"
import { CountryFormData } from "../../types"
import { CalculationsProvider } from "../../contexts"
import { useQuotableCountries } from "../../hooks"
import { CostCalculatorWrapper } from "../CostCalculatorWrapper"

interface CostCalculatorPageLayoutProps {
  /**
   * The URL we should redirect if the user clicks on the "Hire now" link
   * This should only be available in the customer view
   */
  hireUrl?: string
  /** Amplitude event prefix
   * OS - Staff
   * CA - Customer
   **/
  eventPrefix?: string
  /**
   * This prop is used to set the total hiring cost currency code as the company default one, if it exists
   */
  companyCurrencyCode?: string
}

export const CostCalculatorPageLayout = ({
  hireUrl,
  eventPrefix,
  companyCurrencyCode,
}: CostCalculatorPageLayoutProps) => {
  const { t } = useTranslation("components/Quote")

  useEffect(() => {
    amplitudeLogEvent(`${eventPrefix}-view_cost_calculator_page-customer_onboarding`)
  }, [eventPrefix])

  const { data, loading: loadingCountries } = useQuotableCountries()

  // Derived data
  const countryFormData = useMemo<CountryFormData | undefined>(() => {
    if (!data?.quotableCountries) {
      return undefined
    }
    return { label: t("country"), options: data.quotableCountries }
  }, [data, t])

  if (loadingCountries) {
    return <LoadingSpinner />
  }

  if (!countryFormData) {
    return null
  }

  return (
    <Layout align="center">
      <CalculationsProvider
        multiSelectOptions={countryFormData}
        hireUrl={hireUrl}
        eventPrefix={eventPrefix}
        companyCurrencyCode={companyCurrencyCode}
      >
        <CostCalculatorWrapper />
      </CalculationsProvider>
    </Layout>
  )
}
